// src/components/DynamicInputs/CheckboxGroupWithOther.jsx
import React, { useState, useEffect } from 'react';
import CheckboxInput from '../FormElements/CheckboxInput.jsx';
import TextInput from '../FormElements/TextInput.jsx';

const CheckboxGroupWithOther = ({ schema, value: currentCompositeValue, onChange: driverOnChange }) => {
    // Defensive: ensure all objects/arrays are valid
    const {
        options: optionsRaw,
        initialValue: schemaInitialValueRaw,
        componentProps,
        otherInputConfig,
        noneOfTheAboveConfig,
        validation: schemaValidation,
        errorMessages: schemaErrorMessages,
        layout,
        id: questionId,
    } = schema || {};

    const options = Array.isArray(optionsRaw) ? optionsRaw : [];
    const componentPropsSafe = componentProps && typeof componentProps === 'object' ? componentProps : {};
    const otherInputConfigSafe = otherInputConfig && typeof otherInputConfig === 'object' ? otherInputConfig : {};
    const noneOfTheAboveConfigSafe = noneOfTheAboveConfig && typeof noneOfTheAboveConfig === 'object' ? noneOfTheAboveConfig : {};
    const layoutSafe = layout && typeof layout === 'object' ? layout : {};
    const schemaValidationSafe = schemaValidation && typeof schemaValidation === 'object' ? schemaValidation : {};
    const schemaErrorMessagesSafe = schemaErrorMessages && typeof schemaErrorMessages === 'object' ? schemaErrorMessages : {};

    // "Other" config
    const {
        optionValue: otherOptionValue = "other",
        textInputPlaceholder,
        textInputLabel,
        textInputWrapperClassName,
        placeholder: legacyOtherPlaceholder
    } = otherInputConfigSafe;
    const otherPlaceholder = textInputPlaceholder || legacyOtherPlaceholder || "Please specify";

    // "None of the above" config
    const {
        optionValue: noneOfTheAboveValue = "noneOfTheAbove",
        optionLabel: noneOfTheAboveLabel = "Ninguna de las anteriores",
        show: showNoneOfTheAbove = Object.keys(noneOfTheAboveConfigSafe).length > 0
    } = noneOfTheAboveConfigSafe;

    const validation = schemaValidationSafe;
    const errorMessages = schemaErrorMessagesSafe;

    // Defensive: ensure schemaInitialValue is always an object
    const schemaInitialValue = schemaInitialValueRaw && typeof schemaInitialValueRaw === 'object' ? schemaInitialValueRaw : { selections: [], otherDetail: '' };

    const initialSelectionsKey = Object.keys(schemaInitialValue).find(k => Array.isArray(schemaInitialValue[k])) || 'selections';
    const initialOtherDetailKey = Object.keys(schemaInitialValue).find(k => typeof schemaInitialValue[k] === 'string' && k !== initialSelectionsKey) || 'otherDetail';

    const safeInitialValue = { [initialSelectionsKey]: [], [initialOtherDetailKey]: '', ...schemaInitialValue };
    const currentVal = currentCompositeValue || safeInitialValue;

    const currentSelections = currentVal[initialSelectionsKey] || [];
    const currentOtherDetail = currentVal[initialOtherDetailKey] || '';

    const [internalError, setInternalError] = useState('');

    const hasOtherSelected = currentSelections.includes(otherOptionValue);
    const hasNoneOfTheAboveSelected = showNoneOfTheAbove && currentSelections.includes(noneOfTheAboveValue);

    // If "None of the above" is selected, all other checkboxes (except "None of the above" itself) are disabled
    const isOptionDisabled = (optValue) => {
        if (!showNoneOfTheAbove) return false;
        if (optValue === noneOfTheAboveValue) return false;
        return hasNoneOfTheAboveSelected;
    };

    useEffect(() => {
        if (!hasOtherSelected && internalError) {
            setInternalError('');
        }
        if (hasOtherSelected && currentOtherDetail.trim() === '' && validation.otherInputRequired) {
            validateAndReport(currentSelections, currentOtherDetail, true);
        } else if (hasOtherSelected && currentOtherDetail.trim() !== '') {
            setInternalError('');
        }
    }, [hasOtherSelected, currentOtherDetail, internalError, validation.otherInputRequired]);

    const validateAndReport = (newSelections, newOtherDetail) => {
        let error = '';
        const finalOtherDetail = newSelections.includes(otherOptionValue) ? newOtherDetail : ''; // Ensure otherDetail is cleared if 'other' is not selected

        if (validation.required && newSelections.length === 0) {
            error = errorMessages.required || "Selection required.";
        }
        if (!error && validation.minSelections && newSelections.length < validation.minSelections) {
            error = errorMessages.minSelections || `Please select at least ${validation.minSelections} option(s).`;
        }
        if (!error && validation.maxSelections && newSelections.length > validation.maxSelections) {
            error = errorMessages.maxSelections || `Please select no more than ${validation.maxSelections} option(s).`;
        }

        if (!error && newSelections.includes(otherOptionValue) && validation.otherInputRequired && finalOtherDetail.trim() === '') {
            error = errorMessages.otherInputRequired || "Please specify details for 'Other'.";
        }
        if (!error && newSelections.includes(otherOptionValue) && finalOtherDetail.trim() !== '' && validation.otherInputMinLength && finalOtherDetail.length < validation.otherInputMinLength) {
            error = errorMessages.otherInputMinLength || `Other details must be at least ${validation.otherInputMinLength} characters.`;
        }
        if (!error && newSelections.includes(otherOptionValue) && finalOtherDetail.trim() !== '' && validation.otherInputMaxLength && finalOtherDetail.length > validation.otherInputMaxLength) {
            error = errorMessages.otherInputMaxLength || `Other details must not exceed ${validation.otherInputMaxLength} characters.`;
        }

        setInternalError(error);
        driverOnChange(
            { [initialSelectionsKey]: newSelections, [initialOtherDetailKey]: finalOtherDetail },
            error
        );
    };

    const handleCheckboxChange = (optionValue, isChecked) => {
        let newSelections = [...currentSelections];

        if (optionValue === noneOfTheAboveValue) {
            if (isChecked) {
                // Only "None of the above" can be selected, uncheck all others including "Other"
                newSelections = [noneOfTheAboveValue];
            } else {
                newSelections = newSelections.filter(v => v !== noneOfTheAboveValue);
            }
        } else {
            // If any other option is checked, remove "None of the above"
            newSelections = newSelections.filter(v => v !== noneOfTheAboveValue);
            if (isChecked) {
                if (!newSelections.includes(optionValue)) {
                    newSelections.push(optionValue);
                }
            } else {
                newSelections = newSelections.filter(v => v !== optionValue);
            }
        }
        validateAndReport(newSelections, currentOtherDetail);
    };

    const handleOtherInputChange = (event) => {
        const newOtherDetail = event.target.value;
        validateAndReport(currentSelections, newOtherDetail);
    };

    const answerLayout = layoutSafe?.answerLayout || {};
    const containerClassNames = ["mt-3", layoutSafe?.answerOptionsContainerClassName].filter(Boolean).join(" ");
    const checkboxWrapperClassName = componentPropsSafe?.wrapperClassName || "form-check mb-2";
    const checkboxLabelClassName = componentPropsSafe?.labelClassName || "ms-2";

    try {
        return (
            <div className={containerClassNames}>
                {options.map(opt => (
                    <CheckboxInput
                        key={opt.id || opt.value}
                        id={opt.id || `${questionId}-${opt.value}`}
                        name={questionId}
                        value={opt.value}
                        checked={currentSelections.includes(opt.value)}
                        onChange={(e) => handleCheckboxChange(opt.value, e.target.checked)}
                        icon={opt.icon || ''}
                        label={(opt.label) || opt.text}
                        wrapperClassName={checkboxWrapperClassName}
                        labelClassName={checkboxLabelClassName}
                        disabled={isOptionDisabled(opt.value)}
                    />
                ))}
                {/* Always render the "Other" checkbox if otherInputConfig is present and not already in options */}
                {Object.keys(otherInputConfigSafe).length > 0 && otherOptionValue && !options.some(opt => opt.value === otherOptionValue) && (
                    <CheckboxInput
                        key="other-checkbox"
                        id={`${questionId}-${otherOptionValue}`}
                        name={questionId}
                        value={otherOptionValue}
                        checked={currentSelections.includes(otherOptionValue)}
                        onChange={(e) => handleCheckboxChange(otherOptionValue, e.target.checked)}
                        label={otherInputConfigSafe.optionLabel || "Otro"}
                        wrapperClassName={checkboxWrapperClassName}
                        labelClassName={checkboxLabelClassName}
                        disabled={isOptionDisabled(otherOptionValue)}
                    />
                )}
                {Object.keys(otherInputConfigSafe).length > 0 && hasOtherSelected && (
                    <div className={textInputWrapperClassName ? textInputWrapperClassName : "mt-3 ms-4"}>
                        {textInputLabel && (
                            <label htmlFor={`${questionId}-${initialOtherDetailKey}`} className="form-label">
                                {textInputLabel}
                            </label>
                        )}
                        <TextInput
                            id={`${questionId}-${initialOtherDetailKey}`}
                            name={`${questionId}-${initialOtherDetailKey}`}
                            type="text"
                            value={currentOtherDetail}
                            onChange={handleOtherInputChange}
                            placeholder={otherPlaceholder}
                            inputClassName={componentPropsSafe?.otherInputClassName || "form-control"}
                            ariaLabel={textInputLabel || "Other specification"}
                            noWrapper={true}
                        />
                        {internalError && validation.otherInputRequired && (
                            <div className="text-danger mt-1 ms-1" style={{ fontSize: '0.875em' }}>
                                {textInputLabel
                                    ? `Obligatorio`
                                    : internalError
                                }
                            </div>
                        )}
                    </div>
                )}
                {/* Render "None of the above" checkbox if config is present and not already in options, always at the end */}
                {showNoneOfTheAbove && !options.some(opt => opt.value === noneOfTheAboveValue) && (
                    <CheckboxInput
                        key="none-of-the-above-checkbox"
                        id={`${questionId}-${noneOfTheAboveValue}`}
                        name={questionId}
                        value={noneOfTheAboveValue}
                        checked={currentSelections.includes(noneOfTheAboveValue)}
                        onChange={(e) => handleCheckboxChange(noneOfTheAboveValue, e.target.checked)}
                        label={noneOfTheAboveLabel}
                        wrapperClassName={checkboxWrapperClassName}
                        labelClassName={checkboxLabelClassName}
                    />
                )}
                {!hasOtherSelected && internalError && (
                    <div className="text-danger mt-3 ms-1" style={{ fontSize: '0.875em' }}>
                        {internalError}
                    </div>
                )}
            </div>
        );
    } catch (err) {
        // Log error for debugging
        console.error("Error in CheckboxGroupWithOther:", err);
        return <div className="text-danger">Error rendering checkbox group: {err.message}</div>;
    }
};

export default CheckboxGroupWithOther;